import { supabase } from '@/integrations/supabase/client';

// Versioned storage keys - minimized to reduce attack surface
const STORAGE_KEY_TOKEN = 'ta_session_token_v2';
const STORAGE_KEY_EXPIRES = 'ta_session_expires_v2';

// Feature flag for sessionStorage (more secure but doesn't persist across tabs)
// Set to 'true' in localStorage to enable: localStorage.setItem('USE_SESSION_STORAGE', 'true')
const USE_SESSION_STORAGE_FLAG = 'USE_SESSION_STORAGE';

// Debug mode
const DEBUG_SESSION = process.env.NODE_ENV === 'development';

interface BootstrapResult {
  token: string;
  source: 'storage' | 'created';
  playerId: string;
}

/**
 * Get the appropriate storage based on feature flag
 * sessionStorage = cleared on tab close (more secure)
 * localStorage = persists across sessions (better UX)
 */
function getStorage(): Storage {
  try {
    // Check if incognito/strict mode is enabled
    const useSessionStorage = localStorage.getItem(USE_SESSION_STORAGE_FLAG) === 'true';
    return useSessionStorage ? sessionStorage : localStorage;
  } catch {
    return localStorage;
  }
}

/**
 * Centralized session token manager to prevent redundant RPC calls
 * and ensure consistent token handling across the application
 * 
 * SECURITY: Only session_token and expires_at are stored.
 * player_id is fetched from server on each bootstrap via validate_player_session.
 */
class SessionTokenManager {
  private static tokenSet = false;
  private static currentToken: string | null = null;
  private static settingPromise: Promise<boolean> | null = null;
  private static bootstrapPromise: Promise<BootstrapResult | null> | null = null;
  private static bootstrapComplete = false;
  
  // In-memory only - never persisted to storage
  private static cachedPlayerId: string | null = null;
  
  // TTL-based caching for set_player_session_token
  private static tokenSetAt: number = 0;
  private static TOKEN_SET_TTL_MS = 10 * 60 * 1000; // 10 minutes
  
  // TTL-based caching for validate_player_session
  private static validateCache = new Map<string, { result: any; at: number }>();
  private static validateInFlight = new Map<string, Promise<{ data: any; error: any }>>();
  private static VALIDATE_TTL_MS = 60 * 1000; // 60 seconds

  /**
   * Validate a session token with caching and deduplication.
   * TTL: 60 seconds per token.
   * Returns player_id from server (never from client storage).
   */
  private static async validateWithCache(token: string): Promise<{ data: any; error: any }> {
    const cacheKey = token;
    
    // Check cache
    const cached = this.validateCache.get(cacheKey);
    if (cached && (Date.now() - cached.at) < this.VALIDATE_TTL_MS) {
      if (DEBUG_SESSION) {
        console.log('[SessionTokenManager] validate_player_session CACHE HIT', { 
          ageMs: Date.now() - cached.at,
          tokenSuffix: token.slice(-8)
        });
      }
      return { data: cached.result, error: null };
    }
    
    // Check in-flight
    const inFlight = this.validateInFlight.get(cacheKey);
    if (inFlight) {
      if (DEBUG_SESSION) {
        console.log('[SessionTokenManager] validate_player_session IN-FLIGHT REUSE', { tokenSuffix: token.slice(-8) });
      }
      return inFlight;
    }
    
    // Make the request
    const promise = (async () => {
      try {
        const { data, error } = await supabase.rpc('validate_player_session', {
          p_session_token: token
        });
        
        if (!error && data) {
          // Cache successful result
          this.validateCache.set(cacheKey, { result: data, at: Date.now() });
        }
        
        return { data, error };
      } finally {
        this.validateInFlight.delete(cacheKey);
      }
    })();
    
    this.validateInFlight.set(cacheKey, promise);
    return promise;
  }

  /**
   * Check if stored token is expired (client-side check)
   */
  static isTokenExpired(): boolean {
    const storage = getStorage();
    const expiresAt = storage.getItem(STORAGE_KEY_EXPIRES);
    
    if (!expiresAt) return true;
    
    try {
      const expiryDate = new Date(expiresAt);
      // Add 30 second buffer to account for clock drift
      return expiryDate.getTime() - 30000 < Date.now();
    } catch {
      return true;
    }
  }

  /**
   * Bootstrap the session token.
   * 1) Try stored token
   * 2) Validate it with RPC (gets player_id from server)
   * 3) If invalid/missing, return null (caller must re-authenticate)
   * 4) Set token in DB config for RLS
   * 
   * SECURITY: player_id is ALWAYS fetched from server, never trusted from client.
   */
  static async bootstrap(playerId?: string): Promise<BootstrapResult | null> {
    // If already bootstrapping, return the existing promise
    if (this.bootstrapPromise) {
      return this.bootstrapPromise;
    }

    // If already bootstrapped with same player, return immediately
    if (this.bootstrapComplete && this.currentToken && this.cachedPlayerId) {
      if (!playerId || this.cachedPlayerId === playerId) {
        if (DEBUG_SESSION) {
          console.log('[SessionTokenManager] Already bootstrapped for player', this.cachedPlayerId.slice(-6));
        }
        return {
          token: this.currentToken,
          source: 'storage',
          playerId: this.cachedPlayerId
        };
      }
    }

    this.bootstrapPromise = this._doBootstrap();
    const result = await this.bootstrapPromise;
    this.bootstrapPromise = null;
    return result;
  }

  private static async _doBootstrap(): Promise<BootstrapResult | null> {
    if (DEBUG_SESSION) {
      console.log('[SessionTokenManager] Bootstrap starting');
    }

    const storage = getStorage();
    
    // 1) Try to read token from storage
    const storedToken = storage.getItem(STORAGE_KEY_TOKEN);

    // Quick client-side expiry check
    if (storedToken && this.isTokenExpired()) {
      if (DEBUG_SESSION) {
        console.log('[SessionTokenManager] Token expired (client check), clearing');
      }
      this.softReset();
      return null;
    }

    // If token exists, validate it (this gets player_id from server)
    if (storedToken) {
      if (DEBUG_SESSION) {
        console.log('[SessionTokenManager] Found stored token, validating...', storedToken.slice(-8));
      }

      try {
        // Use cached validation with TTL
        const { data, error } = await this.validateWithCache(storedToken);

        if (!error && data && Array.isArray(data) && data.length > 0) {
          // SECURITY: Get player_id from server response, not from storage
          const serverPlayerId = data[0].player_id;
          
          // Token is valid - set it in DB config
          const tokenSet = await this.ensureTokenSet(storedToken);
          
          if (tokenSet) {
            this.bootstrapComplete = true;
            this.cachedPlayerId = serverPlayerId; // In-memory only
            
            if (DEBUG_SESSION) {
              console.log('[SessionTokenManager] Bootstrap OK (storage)', {
                playerId: serverPlayerId.slice(-6),
                tokenSuffix: storedToken.slice(-8),
                source: 'storage'
              });
            }
            
            return {
              token: storedToken,
              source: 'storage',
              playerId: serverPlayerId
            };
          }
        } else {
          if (DEBUG_SESSION) {
            console.log('[SessionTokenManager] Stored token invalid/expired, needs re-auth');
          }
          this.softReset();
        }
      } catch (err) {
        if (DEBUG_SESSION) {
          console.warn('[SessionTokenManager] Token validation failed:', err);
        }
        this.softReset();
      }
    }

    // No valid token - caller must re-authenticate
    return null;
  }

  /**
   * Create a new session after successful authentication.
   * Called by usePlayerAuth after passcode validation.
   */
  static async createSession(playerId: string): Promise<BootstrapResult | null> {
    if (DEBUG_SESSION) {
      console.log('[SessionTokenManager] Creating new session for player', playerId.slice(-6));
    }

    try {
      const { data: sessionData, error: sessionError } = await supabase.rpc('create_player_session', {
        p_player_id: playerId
      });

      if (sessionError || !sessionData || !Array.isArray(sessionData) || sessionData.length === 0) {
        console.error('[SessionTokenManager] Failed to create session:', sessionError);
        return null;
      }

      const session = sessionData[0];
      const newToken = session.session_token;
      const expiresAt = session.expires_at;

      const storage = getStorage();
      
      // SECURITY: Only store token and expiry - NOT player_id
      storage.setItem(STORAGE_KEY_TOKEN, newToken);
      storage.setItem(STORAGE_KEY_EXPIRES, expiresAt);

      // Set token in DB config
      const tokenSet = await this.ensureTokenSet(newToken);
      
      if (!tokenSet) {
        console.error('[SessionTokenManager] Failed to set new token in DB');
        return null;
      }

      this.bootstrapComplete = true;
      this.cachedPlayerId = playerId; // In-memory only

      if (DEBUG_SESSION) {
        console.log('[SessionTokenManager] Session created OK', {
          playerId: playerId.slice(-6),
          tokenSuffix: newToken.slice(-8),
          expiresAt
        });
      }

      return {
        token: newToken,
        source: 'created',
        playerId
      };
    } catch (err) {
      console.error('[SessionTokenManager] Create session error:', err);
      return null;
    }
  }

  /**
   * Check if bootstrap is complete
   */
  static isBootstrapComplete(): boolean {
    return this.bootstrapComplete;
  }

  /**
   * Wait for bootstrap to complete (for components that need to await)
   */
  static async waitForBootstrap(): Promise<boolean> {
    if (this.bootstrapComplete) return true;
    if (this.bootstrapPromise) {
      const result = await this.bootstrapPromise;
      return result !== null;
    }
    return false;
  }

  /**
   * Ensures the session token is set in the database for RLS policies.
   * Uses TTL-based caching: if the same token was set within the last 10 minutes,
   * returns immediately without making an RPC call.
   */
  static async ensureTokenSet(token: string): Promise<boolean> {
    // If we're currently setting this token, return the existing promise
    if (this.settingPromise && this.currentToken === token) {
      return this.settingPromise;
    }

    // TTL-based check: if token is already set and within TTL, skip RPC
    if (this.tokenSet && this.currentToken === token) {
      const age = Date.now() - this.tokenSetAt;
      if (age < this.TOKEN_SET_TTL_MS) {
        if (DEBUG_SESSION) {
          console.log('[SessionTokenManager] set_player_session_token TTL SKIP', { ageMs: age });
        }
        return true;
      }
    }

    // Set the token via RPC
    this.settingPromise = this._setToken(token);
    const result = await this.settingPromise;
    this.settingPromise = null;

    return result;
  }

  private static async _setToken(token: string): Promise<boolean> {
    try {
      const { error } = await supabase.rpc('set_player_session_token', { token });
      
      if (error) {
        if (DEBUG_SESSION) {
          console.error('[SessionTokenManager] Failed to set session token:', error);
        }
        this.tokenSet = false;
        this.currentToken = null;
        return false;
      }

      this.tokenSet = true;
      this.currentToken = token;
      this.tokenSetAt = Date.now(); // Record when token was set for TTL
      return true;
    } catch (error) {
      if (DEBUG_SESSION) {
        console.error('[SessionTokenManager] Error setting session token:', error);
      }
      this.tokenSet = false;
      this.currentToken = null;
      return false;
    }
  }

  /**
   * Soft reset - clears token but keeps session storage preference
   * Used when token expires and user needs to re-authenticate
   */
  static softReset() {
    this.tokenSet = false;
    this.currentToken = null;
    this.settingPromise = null;
    this.bootstrapPromise = null;
    this.bootstrapComplete = false;
    this.tokenSetAt = 0;
    this.cachedPlayerId = null;
    this.validateCache.clear();
    
    const storage = getStorage();
    storage.removeItem(STORAGE_KEY_TOKEN);
    storage.removeItem(STORAGE_KEY_EXPIRES);
    
    // Also clear old v1 keys for migration
    try {
      localStorage.removeItem('ta_session_token_v1');
      localStorage.removeItem('ta_session_expires_v1');
      localStorage.removeItem('ta_session_player_id_v1');
      localStorage.removeItem('player_session_token');
      localStorage.removeItem('player_session_expires');
    } catch { /* ignore */ }
    
    if (DEBUG_SESSION) {
      console.log('[SessionTokenManager] Soft reset - token cleared');
    }
  }

  /**
   * Full reset - clears everything including incognito preference
   */
  static reset() {
    this.softReset();
    
    // Clear incognito mode flag too on full reset
    try {
      localStorage.removeItem(USE_SESSION_STORAGE_FLAG);
    } catch { /* ignore */ }
    
    if (DEBUG_SESSION) {
      console.log('[SessionTokenManager] Full reset complete');
    }
  }

  /**
   * Enable/disable incognito mode (sessionStorage instead of localStorage)
   */
  static setIncognitoMode(enabled: boolean): void {
    try {
      if (enabled) {
        localStorage.setItem(USE_SESSION_STORAGE_FLAG, 'true');
      } else {
        localStorage.removeItem(USE_SESSION_STORAGE_FLAG);
      }
      // Reset to apply the change
      this.softReset();
    } catch { /* ignore */ }
  }

  /**
   * Check if incognito mode is enabled
   */
  static isIncognitoMode(): boolean {
    try {
      return localStorage.getItem(USE_SESSION_STORAGE_FLAG) === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Get the current token if set
   */
  static getCurrentToken(): string | null {
    return this.currentToken;
  }

  /**
   * Check if a token is currently set
   */
  static isTokenSet(): boolean {
    return this.tokenSet;
  }

  /**
   * Get persisted token from storage (without validation)
   */
  static getPersistedToken(): string | null {
    try {
      return getStorage().getItem(STORAGE_KEY_TOKEN);
    } catch {
      return null;
    }
  }

  /**
   * Get cached player ID (in-memory only, not from storage)
   * Returns null if not bootstrapped
   */
  static getCachedPlayerId(): string | null {
    return this.cachedPlayerId;
  }

  /**
   * Legacy method for compatibility - now creates session
   * @deprecated Use createSession instead
   */
  static persistToken(token: string, playerId: string, expiresAt: string): void {
    const storage = getStorage();
    storage.setItem(STORAGE_KEY_TOKEN, token);
    storage.setItem(STORAGE_KEY_EXPIRES, expiresAt);
    // Note: player_id is NOT stored, only kept in memory
    this.cachedPlayerId = playerId;
    this.currentToken = token;
    this.tokenSet = true;
    this.bootstrapComplete = true;
    
    if (DEBUG_SESSION) {
      console.log('[SessionTokenManager] Token persisted (legacy)', {
        playerId: playerId.slice(-6),
        tokenSuffix: token.slice(-8)
      });
    }
  }

  /**
   * @deprecated Use getCachedPlayerId instead
   */
  static getPersistedPlayerId(): string | null {
    return this.cachedPlayerId;
  }
}

export default SessionTokenManager;
